import React, { useMemo } from 'react';
import _ from 'lodash';
import { translate, InjectedTranslateProps } from 'react-i18next';
import {
  IntegrationSection,
  IntegrationRow,
} from './IntegrationSection/IntegrationSection';
import { Heading, Row, Col, Box } from 'wix-style-react';
import { PageComponentId, navigateTo } from '@wix/business-manager-api';
import { IntegrationEntities, FinanceAppData } from '../../../../types';
import { datahooks } from '../../../constants';
import { CrmHomeBiLogger } from '../../../../bi-logger';
import { ReactComponent as GoogleAnaltyticsSVG } from '../../../../assets/images/google-analytics.svg';
import { ReactComponent as FacebookSVG } from '../../../../assets/images/facebook.svg';
import { ReactComponent as GoogleSVG } from '../../../../assets/images/google.svg';
import { SectionTitle } from '../../../SectionTitle/SectionTitle';

export interface IntegrationData {
  type: string;
  navigateOpt: {
    pageComponentId: string;
    appState?: string;
  };
  isConnected: boolean;
  titleText?: string;
  subtitleText?: string;
  imageSrc?: string;
  svg?: JSX.Element;
}

export interface IntegrationSectionsProps extends InjectedTranslateProps {
  financeIntegrationData: FinanceAppData[];
  connections: { [x: string]: boolean };
  biLogger: CrmHomeBiLogger;
}

export const IntegrationsComponent: React.FC<IntegrationSectionsProps> = (
  props,
) => {
  const { t, biLogger } = props;

  const data = useMemo(
    () => ({
      communication: [
        {
          type: 'gmail',
          imageSrc: '',
          navigateOpt: {
            pageComponentId: PageComponentId.Engage,
            appState: 'settings/Email',
          },
          isConnected: !!props.connections[IntegrationEntities.GMAIL],
          svg: <GoogleSVG />,
        },
        {
          type: 'facebook',
          imageSrc: '',
          navigateOpt: {
            pageComponentId: PageComponentId.Engage,
            appState: 'settings/integrations',
          },
          isConnected: !!props.connections[IntegrationEntities.FACEBOOK_PAGE],
          svg: <FacebookSVG />,
        },
      ],
      marketing: [
        {
          type: 'googleAnalytics',
          imageSrc: '',
          navigateOpt: {
            pageComponentId: PageComponentId.MarketingIntegration,
            appState: 'widget/googleAnalytics',
          },
          isConnected: !!props.connections[
            IntegrationEntities.GOOGLE_ANALYTICS
          ],
          svg: <GoogleAnaltyticsSVG />,
        },
        {
          type: 'facebookPixel',
          imageSrc: '',
          navigateOpt: {
            pageComponentId: PageComponentId.MarketingIntegration,
            appState: 'widget/facebookPixel',
          },
          isConnected: !!props.connections[IntegrationEntities.FACEBOOK_PIXEL],
          svg: <FacebookSVG />,
        },
      ],
      finances: props.financeIntegrationData.slice(0, 2).map((appData) => ({
        type: appData.slug,
        imageSrc: appData.icon,
        titleText: appData.name,
        subtitleText: appData.description,
        navigateOpt: {
          pageComponentId: PageComponentId.AppMarket,
          appState: `${appData.slug}/overview`,
        },
        isConnected: !!appData.installed,
      })),
    }),
    [props.connections, props.financeIntegrationData],
  );
  const reportBiRow = (type: string, category: String) => {
    switch (category) {
      case CATEGORIES.COMMUNICATION:
        biLogger.reportConnectToCommunicatation(type);
        break;
      case CATEGORIES.MARKEING:
        biLogger.reportConnectToMarketing(type);
        break;
      case CATEGORIES.FINANCE:
        biLogger.reportConnectToFinance(type);
        break;
      default:
    }
  };

  const renderRow = (integrationRow: IntegrationData, category: string) => {
    return (
      <IntegrationRow
        key={`${category}-${integrationRow.type}`}
        imageSrc={integrationRow.imageSrc}
        title={
          integrationRow.titleText ||
          t(`integrations.${category}.${integrationRow.type}.title`)
        }
        subtitle={
          integrationRow.subtitleText ||
          t(`integrations.${category}.${integrationRow.type}.subtitle`)
        }
        name={`${category}-${integrationRow.type}`}
        isConnected={integrationRow.isConnected}
        svg={integrationRow.svg}
        actionOnClick={() => {
          reportBiRow(integrationRow.type, category);
          navigateTo({
            pageComponentId: integrationRow.navigateOpt.pageComponentId,
            contextData: {
              appState: integrationRow.navigateOpt.appState,
              referrer: 'crmHome',
            },
          });
        }}
      />
    );
  };

  return (
    <Row>
      <Col>
        <SectionTitle
          title={t('integrations.title')}
          dataHook={datahooks.integrationsTitle}
        />
        <IntegrationSection
          name={CATEGORIES.COMMUNICATION}
          onViewMoreClicked={() => {
            biLogger.reportViewAllCommunicatation();
            navigateTo({
              pageComponentId: PageComponentId.Engage,
              contextData: {
                appState: 'settings/integrations',
                referrer: 'crmHome',
              },
            });
          }}
        >
          {data.communication.map((row) =>
            renderRow(row, CATEGORIES.COMMUNICATION),
          )}
        </IntegrationSection>
        <IntegrationSection
          name={CATEGORIES.MARKEING}
          onViewMoreClicked={() => {
            biLogger.reportViewAllMarketing();
            navigateTo({
              pageComponentId: PageComponentId.MarketingIntegration,
              contextData: {
                referrer: 'crmHome',
              },
            });
          }}
        >
          {data.marketing.map((row) => renderRow(row, CATEGORIES.MARKEING))}
        </IntegrationSection>
        {!_.isEmpty(data.finances) && (
          <IntegrationSection
            name={CATEGORIES.FINANCE}
            onViewMoreClicked={() => {
              biLogger.reportViewAllFinance();
              navigateTo({
                pageComponentId: PageComponentId.Integrations,
              });
            }}
          >
            {data.finances.map((row) => renderRow(row, CATEGORIES.FINANCE))}
          </IntegrationSection>
        )}
      </Col>
    </Row>
  );
};

export const Integrations = translate()(IntegrationsComponent);

export const CATEGORIES = {
  FINANCE: 'finance',
  MARKEING: 'marketing',
  COMMUNICATION: 'communication',
};
